import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import SegmentControl from "Components/Sales/SegmentControl";
import SalesTrendCards from "Components/Sales/SalesTrendCards";
import SalesTrendTable from "Components/Sales/SalesTrendTable";
import TrendLineChart from "Components/Sales/charts/TrendLineChart";
import useSalesFilter from "utils/useSalesFilter";
import unwrapResponse from "utils/unwrapResponse";
import { catchError } from "JS/common";
import { getSalesTrend } from "JS/API";

const METRIC_OPTIONS = [
  { key: "both", label: "매출 + 순매출" },
  { key: "gross", label: "매출" },
  { key: "net", label: "순매출" },
];

const styles = {
  toolbar: {
    display: "flex",
    gap: "14px",
    alignItems: "center",
    flexWrap: "wrap",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "14px 18px",
    marginBottom: "20px",
  },
  yearNav: { display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", fontSize: "16px", color: "#1f2937" },
  navButton: {
    width: "28px",
    height: "28px",
    backgroundColor: "#f3f4f6",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
  },
  label: { fontSize: "13px", color: "#6b7280" },
  select: {
    padding: "7px 10px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "inherit",
    backgroundColor: "#ffffff",
    color: "#1f2937",
  },
  chartCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "22px 24px",
    marginBottom: "20px",
  },
  emptyHint: { fontSize: "13px", color: "#9ca3af" },
  chartTitle: { fontSize: "16px", fontWeight: "600", color: "#1f2937", margin: "0 0 4px" },
  chartDesc: { fontSize: "13px", color: "#6b7280", margin: "0 0 12px" },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "80px",
    color: "#9ca3af",
    fontSize: "14px",
  },
};

const SalesTrendTab = ({ service }) => {
  const navigate = useNavigate();
  const { year, compareMode, compareYear, updateParams } = useSalesFilter();

  const [metric, setMetric] = useState("both");
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getSalesTrend(year, service, compareYear);
      if (typeof result !== "object" || !result?.data) {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      setData(unwrapResponse(result));
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [year, service, compareYear, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  const compareOptions = useMemo(
    () => [
      { key: "prev", label: `전년 (${year - 1})` },
      { key: "pick", label: "직접 선택" },
      { key: "none", label: "없음" },
    ],
    [year]
  );

  const pickableYears = useMemo(
    () => (data?.available_years || []).filter((y) => y !== year).sort((a, b) => b - a),
    [data, year]
  );

  const handleCompareMode = useCallback(
    (mode) => {
      if (mode === "prev") updateParams({ compare: null });
      else if (mode === "none") updateParams({ compare: "none" });
      else {
        const first = pickableYears.find((y) => y !== year - 1) ?? pickableYears[0];
        updateParams({ compare: first ?? "pick" });
      }
    },
    [updateParams, pickableYears, year]
  );

  const now = new Date();
  const inProgressMonth = year === now.getFullYear() ? now.getMonth() + 1 : null;
  const totalCount = useMemo(() => {
    const through = data?.ytd?.through_month ?? 12;
    return (data?.months || [])
      .filter((m) => m.month <= through)
      .reduce((acc, m) => acc + (m.count || 0), 0);
  }, [data]);

  return (
    <div>
      <div style={styles.toolbar}>
        <div style={styles.yearNav}>
          <button type="button" aria-label="이전 연도" style={styles.navButton} onClick={() => updateParams({ year: year - 1 })}>
            <FaChevronLeft size={12} />
          </button>
          <span>{year}년</span>
          <button type="button" aria-label="다음 연도" style={styles.navButton} onClick={() => updateParams({ year: year + 1 })}>
            <FaChevronRight size={12} />
          </button>
        </div>
        <span style={styles.label}>지표</span>
        <SegmentControl options={METRIC_OPTIONS} value={metric} onChange={setMetric} compact />
        <span style={styles.label}>비교 대상</span>
        <SegmentControl options={compareOptions} value={compareMode} onChange={handleCompareMode} compact />
        {compareMode === "pick" &&
          (pickableYears.length > 0 ? (
            <select
              aria-label="비교 연도"
              style={styles.select}
              value={compareYear ?? ""}
              onChange={(e) => updateParams({ compare: e.target.value })}
            >
              {pickableYears.map((y) => (
                <option key={y} value={y}>{y}년</option>
              ))}
            </select>
          ) : (
            <span style={styles.emptyHint}>비교할 다른 연도의 데이터가 없어요</span>
          ))}
      </div>

      {isLoading || !data ? (
        <div style={styles.loading}>로딩 중...</div>
      ) : (
        <>
          <SalesTrendCards year={year} compareYear={data.compare?.year ?? null} ytd={data.ytd} totalCount={totalCount} />

          <div style={styles.chartCard}>
            <h2 style={styles.chartTitle}>월별 매출 · 순매출 추이</h2>
            <p style={styles.chartDesc}>
              단위: 원 · 실선 {year}년{data.compare ? `, 점선 ${data.compare.year}년` : ""} · 환불은 환불 발생 월에 차감
            </p>
            <TrendLineChart year={year} months={data.months} compare={data.compare} metric={metric} />
          </div>

          <SalesTrendTable
            year={year}
            months={data.months}
            compare={data.compare}
            throughMonth={data.ytd?.through_month ?? 12}
            inProgressMonth={inProgressMonth}
          />
        </>
      )}

      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={handleAlertOk} />
      )}
    </div>
  );
};

export default SalesTrendTab;
